'use client';

import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import { createContext, useContext } from 'react';
import { NavLink, type NavLinkProps } from 'react-router-dom';
import { cn } from 'v5/platform/dom/cn';
import { focusRings } from './_common/focusRing';

const Tabs = TabsPrimitive.Root;

/** `compact` sits between `sm` and `md`. */
type TabsSize = 'sm' | 'compact' | 'md' | 'lg';

type TabsChromeVariant = 'default' | 'inline' | 'floating' | 'docked';

interface TabsContextValue {
  size?: TabsSize;
  variant?: TabsChromeVariant;
}

const TabsContext = createContext<TabsContextValue>({});

const tabsListVariants = cva('inline-flex items-center justify-center', {
  variants: {
    variant: {
      default: 'rounded-md bg-bg-subtle p-1 gap-1',
      inline:
        'border-b-2 bg-bg-default border-bg-default text-fg-muted w-full justify-start gap-5 px-5',
      floating: [
        'rounded-full bg-bg-default/50 backdrop-blur-md border border-border-default shadow-[0_8px_32px_rgba(0,0,0,0.08),0_2px_8px_rgba(0,0,0,0.04)] p-2.5 gap-0.5',
        'outline-none transition-[box-shadow,ring-width,ring-offset-width]',
        '[&:has(:focus-visible)]:ring-2 [&:has(:focus-visible)]:ring-ring [&:has(:focus-visible)]:ring-offset-2 [&:has(:focus-visible)]:ring-offset-bg-default'
      ],
      docked: [
        'inline-flex h-full min-h-11 w-auto items-stretch justify-start gap-0 rounded-none border-0 bg-transparent p-0'
      ]
    },
    size: {
      sm: 'h-9',
      compact: 'h-10',
      md: '',
      lg: ''
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'md'
  },
  compoundVariants: [
    {
      variant: 'docked',
      size: 'sm',
      className: 'h-auto min-h-11'
    },
    {
      variant: 'docked',
      size: 'md',
      className: 'h-auto min-h-11'
    },
    {
      variant: 'docked',
      size: 'lg',
      className: 'h-auto min-h-11'
    }
  ]
});

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    variant?: TabsChromeVariant;
    size?: TabsSize;
  }
>(({ className, variant, size, ...props }, ref) => (
  <TabsContext.Provider value={{ size, variant }}>
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabsListVariants({ variant, size }), className)}
      {...props}
    />
  </TabsContext.Provider>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const tabsTriggerVariants = cva(
  [
    'inline-flex items-center justify-center whitespace-nowrap disabled:pointer-events-none disabled:opacity-50 transition-all duration-200'
  ],
  {
    variants: {
      variant: {
        default: [
          focusRings.focus.visible,
          'rounded px-4 text-base font-medium data-[state=active]:bg-bg-default data-[state=active]:text-fg-default data-[state=active]:shadow-sm',
          'aria-[current=page]:bg-bg-default aria-[current=page]:text-fg-default aria-[current=page]:shadow-sm',
          'not-data-[state=active]:not-aria-[current=page]:hover:bg-bg-inset/50 not-data-[state=active]:not-aria-[current=page]:hover:text-fg-default'
        ],
        inline: [
          'focus-visible:border-border-default',
          '-mb-[2px] px-0 text-base font-bold data-[state=active]:text-fg-default border-b-2 border-bg-default data-[state=active]:border-bg-emphasis ',
          'aria-[current=page]:text-fg-default aria-[current=page]:border-bg-emphasis',
          'not-data-[state=active]:not-aria-[current=page]:hover:text-fg-default'
        ],
        floating: [
          focusRings.focus.visible,
          'rounded-full tabular-nums transition-all duration-200 border border-transparent',
          'text-fg-muted',
          'data-[state=active]:text-white data-[state=active]:bg-brand-600 data-[state=active]:border-border-default',
          'data-[state=active]:shadow-[0_2px_12px_rgba(0,0,0,0.08),0_1px_3px_rgba(0,0,0,0.06)]',
          'data-[state=active]:hover:bg-brand-700',
          'data-[state=inactive]:hover:text-fg-default data-[state=inactive]:hover:bg-bg-subtle',
          'aria-[current=page]:text-white aria-[current=page]:bg-brand-600 aria-[current=page]:border-border-default',
          'aria-[current=page]:shadow-[0_2px_12px_rgba(0,0,0,0.08),0_1px_3px_rgba(0,0,0,0.06)]',
          'aria-[current=page]:hover:bg-brand-700'
        ],
        docked: [
          focusRings.focus.visible,
          'relative rounded-none tabular-nums transition-colors duration-200',
          'box-border border-border-default border-r first:border-l',
          'text-fg-muted',
          'data-[state=active]:font-semibold data-[state=active]:text-fg-default',
          'data-[state=inactive]:hover:text-fg-default',
          'aria-[current=page]:font-semibold aria-[current=page]:text-fg-default'
        ]
      },
      size: {
        sm: 'py-0.5 px-2',
        compact: 'py-1 px-3',
        md: 'py-2 px-4',
        lg: 'py-4 px-5 text-md font-bold'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'md'
    },
    compoundVariants: [
      {
        variant: 'inline',
        size: 'sm',
        className: 'py-1.5'
      },
      {
        variant: 'inline',
        size: 'md',
        className: 'py-3.5'
      },
      {
        variant: 'floating',
        size: 'md',
        className: 'py-2.5 px-5'
      },
      {
        variant: 'docked',
        size: 'sm',
        className: 'min-h-10 px-3 py-2 text-sm'
      },
      {
        variant: 'docked',
        size: 'md',
        className: 'min-h-11 px-3 py-2.5 text-sm'
      },
      {
        variant: 'docked',
        size: 'lg',
        className: 'min-h-12 px-5 py-3 text-base font-semibold'
      }
    ]
  }
);

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    variant?: TabsChromeVariant;
    size?: 'sm' | 'compact' | 'md';
  }
>(({ className, variant: variantProp, size: sizeProp, ...props }, ref) => {
  const context = useContext(TabsContext);
  const variant = variantProp ?? context.variant ?? 'default';
  const size = sizeProp ?? context.size ?? 'md';

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerVariants({ variant, size }), className)}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      focusRings.focus.visible,
      // A panel fills its container, which often clips or scrolls, so the ring is drawn inside.
      'not-disable-focus-ring:not-aria-readonly:focus-visible:ring-inset not-disable-focus-ring:not-aria-readonly:focus-visible:ring-offset-0',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

function toPathString(to: NavLinkProps['to']): string {
  if (typeof to === 'string') {
    return to;
  }
  if (to && typeof to === 'object' && 'pathname' in to) {
    const d = to as { pathname: string; search?: string; hash?: string };
    return `${d.pathname}${d.search ?? ''}${d.hash ?? ''}`;
  }
  return '';
}

function defaultNavTabIsActive(pathname: string, toHref: string) {
  return pathname === toHref || pathname.startsWith(`${toHref}/`);
}

type NavTabContextValue = TabsContextValue;

const NavTabContext = createContext<NavTabContextValue>({});

const NavTabList = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    variant?: TabsChromeVariant;
    size?: 'sm' | 'md' | 'lg';
  }
>(({ className, variant = 'default', size = 'md', ...props }, ref) => (
  <NavTabContext.Provider value={{ variant, size }}>
    <div
      ref={ref}
      className={cn(tabsListVariants({ variant, size }), className)}
      {...props}
    />
  </NavTabContext.Provider>
));
NavTabList.displayName = 'NavTabList';

export type NavTabTriggerProps = Omit<NavLinkProps, 'className'> & {
  className?: string;
  variant?: TabsChromeVariant;
  size?: 'sm' | 'md';
};

const NavTabTrigger = React.forwardRef<HTMLAnchorElement, NavTabTriggerProps>(
  (
    {
      className,
      variant: variantProp,
      size: sizeProp,
      isActive: isActiveProp,
      to,
      ...rest
    },
    ref
  ) => {
    const ctx = useContext(NavTabContext);
    const variant = variantProp ?? ctx.variant ?? 'default';
    const size = sizeProp ?? ctx.size ?? 'md';
    const toHref = toPathString(to);

    const isActiveFn: NonNullable<NavLinkProps['isActive']> = (
      match,
      location
    ) =>
      isActiveProp
        ? isActiveProp(match, location)
        : defaultNavTabIsActive(location.pathname, toHref);

    return (
      <NavLink
        ref={ref}
        to={to}
        isActive={isActiveFn}
        className={cn(tabsTriggerVariants({ variant, size }), className)}
        {...rest}
      />
    );
  }
);
NavTabTrigger.displayName = 'NavTabTrigger';

/** The chrome of the tab list around the caller (`TabsList` or `NavTabList`). */
function useTabsChrome(): { variant: TabsChromeVariant; size: TabsSize } {
  const tabs = useContext(TabsContext);
  const nav = useContext(NavTabContext);
  return {
    variant: tabs.variant ?? nav.variant ?? 'default',
    size: tabs.size ?? nav.size ?? 'md'
  };
}

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  NavTabList,
  NavTabTrigger,
  useTabsChrome
};
export { tabsListVariants, tabsTriggerVariants };
